import { ref } from 'vue'
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging'
import { api } from '@/services/api'
import { useToastStore } from '@/stores/toast'
import { isNativePlatform } from '@/utils/platform'
import { useNativePushNotifications } from '@/composables/useNativePushNotifications'

const TOKEN_STORAGE_KEY = 'dhikr_fcm_token'

// Module-level singletons
let firebaseApp: FirebaseApp | undefined = undefined
let messaging: Messaging | null = null
let swRegistration: ServiceWorkerRegistration | null = null

const isSupported = ref(false)
const permissionState = ref<NotificationPermission>('default')
const fcmToken = ref<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY))

interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
  vapidKey: string
}

// Cached config — fetched once from API, falls back to env vars
let cachedConfig: FirebaseWebConfig | null = null

function getEnvConfig(): FirebaseWebConfig {
  const env = (window as any).ENV_CONFIG || {}
  return {
    apiKey: env.VITE_FIREBASE_API_KEY || import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: env.VITE_FIREBASE_PROJECT_ID || import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: env.VITE_FIREBASE_APP_ID || import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID || import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
    vapidKey: env.VITE_FIREBASE_VAPID_KEY || import.meta.env.VITE_FIREBASE_VAPID_KEY || '',
  }
}

async function fetchFirebaseConfig(): Promise<FirebaseWebConfig> {
  if (cachedConfig) return cachedConfig

  try {
    const response = await api.getFirebaseConfig()
    const remote = response.data?.data
    if (remote?.apiKey) {
      // Merge: remote values take priority, env vars fill any gaps
      const env = getEnvConfig()
      cachedConfig = {
        apiKey: remote.apiKey || env.apiKey,
        authDomain: remote.authDomain || env.authDomain,
        projectId: remote.projectId || env.projectId,
        storageBucket: remote.storageBucket || env.storageBucket,
        messagingSenderId: remote.messagingSenderId || env.messagingSenderId,
        appId: remote.appId || env.appId,
        measurementId: remote.measurementId || env.measurementId,
        vapidKey: remote.vapidKey || env.vapidKey,
      }
      return cachedConfig
    }
  } catch (err) {
    console.warn('Failed to fetch Firebase config from API, falling back to env vars:', err)
  }

  cachedConfig = getEnvConfig()
  return cachedConfig
}

export function useFirebaseMessaging() {
  const toast = useToastStore()
  const nativePush = useNativePushNotifications()

  async function initialize(): Promise<boolean> {
    if (isNativePlatform) {
      return nativePush.initialize()
    }
    // Check browser support
    if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) {
      console.warn('Push notifications not supported in this browser')
      isSupported.value = false
      return false
    }

    const config = await fetchFirebaseConfig()
    if (!config.apiKey || !config.projectId || !config.messagingSenderId) {
      console.warn('Firebase config not set — push notifications disabled')
      isSupported.value = false
      return false
    }

    isSupported.value = true
    permissionState.value = Notification.permission

    // Initialize Firebase app
    if (getApps().length === 0) {
      firebaseApp = initializeApp(config)
    } else {
      firebaseApp = getApps()[0]!
    }

    // Register service worker
    try {
      swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
      // Send Firebase config to the service worker
      swRegistration.active?.postMessage({
        type: 'FIREBASE_CONFIG',
        config,
      })
      // Also send when SW activates (first install)
      navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({
          type: 'FIREBASE_CONFIG',
          config,
        })
      })
    } catch (err) {
      console.error('Failed to register service worker:', err)
      return false
    }

    // Initialize messaging
    messaging = getMessaging(firebaseApp)

    // Listen for foreground messages
    onMessage(messaging, (payload) => {
      const title = payload.notification?.title || payload.data?.title || 'Dhikr Session'
      const body = payload.notification?.body || payload.data?.body || ''
      toast.show(body ? `${title}: ${body}` : title, 'info')
    })

    return true
  }

  async function requestPermissionAndGetToken(): Promise<string | null> {
    if (isNativePlatform) {
      return nativePush.requestPermissionAndGetToken()
    }

    if (!isSupported.value || !messaging || !swRegistration) {
      const ok = await initialize()
      if (!ok) return null
    }

    // Request notification permission
    const permission = await Notification.requestPermission()
    permissionState.value = permission

    if (permission !== 'granted') {
      console.info('Notification permission denied')
      return null
    }

    const vapidKey = cachedConfig?.vapidKey || getEnvConfig().vapidKey
    if (!vapidKey) {
      console.warn('VAPID key not configured — cannot get FCM token')
      return null
    }

    try {
      const token = await getToken(messaging!, {
        vapidKey,
        serviceWorkerRegistration: swRegistration!,
      })

      if (token) {
        fcmToken.value = token
        localStorage.setItem(TOKEN_STORAGE_KEY, token)
        return token
      }
    } catch (err) {
      console.error('Failed to get FCM token:', err)
    }

    return null
  }

  async function registerDeviceToken(): Promise<void> {
    if (isNativePlatform) {
      await nativePush.requestPermissionAndGetToken()
      return
    }

    if (!isSupported.value || !messaging || !swRegistration) {
      const ok = await initialize()
      if (!ok) return
    }

    const token = await requestPermissionAndGetToken()
    if (!token) return

    // Skip if we already sent this token
    const lastSent = localStorage.getItem('dhikr_fcm_token_sent')
    if (lastSent === token) return

    try {
      await api.updateDeviceToken(token)
      localStorage.setItem('dhikr_fcm_token_sent', token)
    } catch (err) {
      console.error('Failed to register device token with server:', err)
    }
  }

  function clearToken() {
    if (isNativePlatform) {
      nativePush.clearToken()
      return
    }

    fcmToken.value = null
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem('dhikr_fcm_token_sent')
  }

  return {
    isSupported: isNativePlatform ? nativePush.isSupported : isSupported,
    permissionState: isNativePlatform ? nativePush.permissionState : permissionState,
    fcmToken: isNativePlatform ? nativePush.fcmToken : fcmToken,
    initialize,
    requestPermissionAndGetToken,
    registerDeviceToken,
    clearToken,
  }
}
